import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { attendanceAPI, breaksAPI, categoriesAPI } from '../services/api';
import { formatMinutes, timeLabel } from '../components/attendance/format';
import toast from 'react-hot-toast';

export default function SimpleAttendance() {
  const [now, setNow] = useState(new Date());
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [categories, setCategories] = useState(null);
  const credentials = useRef(null);
  const generation = useRef(0);
  const lock = useRef(false);
  const numberInput = useRef(null);
  const loggedIn = Boolean(profile);
  const reset = useCallback(() => {
    generation.current += 1; credentials.current = null; lock.current = false;
    setProfile(null); setEmployeeNumber(''); setPin(''); setCategories(null);
    setReceipt(null); setError(''); setBusy(false); toast.dismiss();
  }, []);
  useEffect(() => { const timer = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(timer); }, []);
  useEffect(() => {
    if (!loggedIn) return;
    let timer;
    const touch = () => { clearTimeout(timer); timer = setTimeout(reset, 120000); };
    const hide = () => { if (document.hidden) reset(); };
    touch(); window.addEventListener('pointerdown', touch); window.addEventListener('keydown', touch);
    document.addEventListener('visibilitychange', hide);
    const interval = setInterval(async () => {
      const version = generation.current;
      if (!credentials.current || lock.current) return;
      try {
        const result = await api.post('/attendance/kiosk/overview', credentials.current);
        if (version === generation.current) { setProfile(result.data); setError(''); }
      } catch (e) {
        if (version !== generation.current) return;
        if ([401, 403].includes(e.response?.status)) reset();
        else setError('Aktualisierung fehlgeschlagen. Bitte erneut anmelden.');
      }
    }, 30000);
    return () => { clearTimeout(timer); clearInterval(interval); window.removeEventListener('pointerdown', touch); window.removeEventListener('keydown', touch); document.removeEventListener('visibilitychange', hide); };
  }, [loggedIn, reset]);
  useEffect(() => { if (!receipt) return; const timer = setTimeout(reset, 30000); return () => clearTimeout(timer); }, [receipt, reset]);
  async function login(e) {
    e.preventDefault();
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    const version = generation.current;
    const data = { employeeNumber: employeeNumber.trim(), pin: pin.trim() };
    try {
      const result = await api.post('/attendance/kiosk/overview', data);
      if (version !== generation.current) return;
      credentials.current = data; setProfile(result.data); setEmployeeNumber(''); setPin('');
    } catch (e) {
      if (version === generation.current) { setPin(''); setError(e.response?.data?.error || 'Anmeldung fehlgeschlagen.'); }
    } finally { if (version === generation.current) { lock.current = false; setBusy(false); } }
  }
  async function act(action, categoryId) {
    if (lock.current || !credentials.current) return;
    lock.current = true; setBusy(true); setError('');
    const version = generation.current;
    const data = credentials.current;
    try {
      if (action === 'pause' && !categoryId) {
        const result = await categoriesAPI.kioskGetAll(data);
        if (version !== generation.current) return;
        const available = result.data.data.breakCategories;
        if (!available.length) throw new Error('Keine Pausenkategorie eingerichtet. Bitte die Verwaltung kontaktieren.');
        if (available.length > 1) { setCategories(available); return; }
        categoryId = available[0].id;
      }
      let result;
      if (action === 'in') result = await attendanceAPI.kioskClockIn(data);
      if (action === 'out') result = await attendanceAPI.kioskClockOut(data);
      if (action === 'pause') result = await breaksAPI.kioskStart({ ...data, categoryId });
      if (action === 'resume') result = await breaksAPI.kioskStop(data);
      if (version !== generation.current) return;
      if (action === 'out') { reset(); setReceipt(result.data); return; }
      toast.success(action === 'resume' ? 'Pause beendet.' : action === 'pause' ? 'Pause gestartet.' : 'Eingestempelt.');
      setCategories(null);
      const refreshed = await api.post('/attendance/kiosk/overview', data);
      if (version === generation.current) setProfile(refreshed.data);
    } catch (e) {
      if (version !== generation.current) return;
      if ([401, 403].includes(e.response?.status)) reset();
      else setError(e.response?.data?.error || e.message || 'Aktion fehlgeschlagen.');
    } finally { if (version === generation.current) { lock.current = false; setBusy(false); } }
  }
  const current = profile?.records.find(r => !r.clock_out);
  return <div className="min-h-screen bg-slate-100 text-slate-900 p-4 md:p-8">
    <header className="max-w-3xl mx-auto flex flex-wrap items-center justify-between gap-4 mb-8">
      <h1 className="text-3xl font-bold">Anwesenheit & Pausen</h1>
      <div className="text-right"><div className="text-3xl font-mono">{now.toLocaleTimeString('de-DE')}</div><Link className="text-blue-700 text-sm" onClick={reset} to="/dashboard">Zur Verwaltung</Link></div>
    </header>
    <main className="max-w-3xl mx-auto space-y-5">
      {error && <p role="alert" className="bg-red-50 text-red-800 p-4 rounded-xl">{error}</p>}
      {!profile && !receipt && <section className="bg-white rounded-2xl shadow-sm p-6 max-w-md mx-auto">
        <h2 className="text-xl font-semibold mb-5">Persönlich anmelden</h2>
        <form onSubmit={login} className="space-y-4">
          <label className="block">Personalnummer<input ref={numberInput} autoFocus required autoComplete="off" className="mt-1 w-full border rounded-lg p-3 text-xl" value={employeeNumber} disabled={busy} onChange={e => setEmployeeNumber(e.target.value)} /></label>
          <label className="block">PIN<input required type="password" autoComplete="off" className="mt-1 w-full border rounded-lg p-3 text-xl" value={pin} disabled={busy} onChange={e => setPin(e.target.value)} /></label>
          <button type="submit" disabled={busy} className="w-full p-4 rounded-xl bg-slate-800 text-white font-semibold disabled:opacity-50">{busy ? 'Anmeldung …' : 'Meine Zeiten öffnen'}</button>
        </form>
      </section>}
      {profile && <>
        <section className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex justify-between items-start gap-3"><div><h2 className="text-2xl font-semibold">Hallo, {profile.user.first_name}!</h2><p className="mt-2 text-slate-500">{current ? profile.on_break ? 'Du bist in Pause.' : 'Du bist eingestempelt.' : 'Du bist ausgestempelt.'}</p></div><button onClick={reset} className="border rounded-lg px-4 py-2">Abmelden</button></div>
          {current && <><p className="mt-6 text-sm text-slate-500">Eingestempelt seit {timeLabel(current.clock_in)}</p><p className="text-3xl font-bold my-3">{formatMinutes((now - new Date(current.clock_in)) / 60000)}</p><div className="grid grid-cols-2 gap-4 mb-6"><div>Erfasste Pausen<p className="font-semibold">{formatMinutes(current.break_minutes)}</p></div><div>Arbeitszeit ohne Pausen<p className="font-semibold">{formatMinutes(current.work_minutes)}</p></div></div><p className="text-xs text-slate-500 mb-4">Pausen und Arbeitszeit werden alle 30 Sekunden aktualisiert.</p></>}

          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            {!current && <button disabled={busy} onClick={() => act('in')} className="p-4 rounded-xl bg-green-700 text-white font-semibold disabled:opacity-50">Einstempeln</button>}
            {current && <><button disabled={busy} onClick={() => act('out')} className="p-4 rounded-xl bg-slate-800 text-white font-semibold disabled:opacity-50">Ausstempeln</button><button disabled={busy} onClick={() => act(profile.on_break ? 'resume' : 'pause')} className="p-4 rounded-xl bg-amber-100 text-amber-900 font-semibold disabled:opacity-50">{profile.on_break ? 'Pause beenden' : 'Pause starten'}</button></>}
          </div>
          {categories && <div className="mt-4 space-y-2"><p>Welche Pause?</p>{categories.map(c => <button key={c.id} disabled={busy} className="block w-full border rounded-lg p-3" onClick={() => act('pause', c.id)}>{c.name}</button>)}<button onClick={() => setCategories(null)} className="text-sm p-2">Abbrechen</button></div>}
          <p className="text-xs text-slate-400 mt-5">Automatische Abmeldung nach 2 Minuten ohne Bedienung.</p>
        </section>

      </>}
      {receipt && <section role="dialog" aria-modal="true" aria-labelledby="receipt-title" className="bg-white rounded-2xl shadow-xl p-8 max-w-lg mx-auto text-center">
        <h2 id="receipt-title" className="text-2xl font-bold">Schönen Feierabend, {receipt.attendance.first_name}!</h2>
        <p className="mt-3 text-lg text-slate-600">Vielen Dank für deinen Einsatz</p>
        <p className="mt-4 text-slate-500">Du warst eingestempelt für</p><p className="text-4xl font-bold text-green-700 my-3">{formatMinutes(receipt.summary.attendance_minutes)}</p>
        <p>{timeLabel(receipt.attendance.clock_in)} – {timeLabel(receipt.attendance.clock_out)}</p>
        <div className="grid grid-cols-2 gap-4 bg-slate-50 rounded-xl p-5 my-6"><div>Pause<p className="font-semibold">{formatMinutes(receipt.summary.break_minutes)}</p></div><div>Arbeitszeit ohne Pausen<p className="font-semibold">{formatMinutes(receipt.summary.work_minutes)}</p></div></div>
        <button autoFocus onClick={reset} className="bg-slate-800 text-white w-full p-3 rounded-xl">Fertig</button><p className="text-xs text-slate-400 mt-3">Schließt automatisch nach 30 Sekunden.</p>
      </section>}
    </main>
  </div>;
}
